/*
 * ALOHA QUEEN SUPERNOVA - User Profile
 * Copyright © 2025 Malcolm KH Lee III. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import RealisticGalaxyBackground from '../components/RealisticGalaxyBackground';

const Profile = () => {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: '',
    avatar_preference: 'queen',
    theme: 'dark'
  });
  const [avatarImage, setAvatarImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setFormData({
      full_name: user.full_name || '',
      avatar_preference: user.avatar_preference || 'queen',
      theme: user.theme || 'dark'
    });
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/profile/update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        updateProfile(data.user);
        setMessage('Profile updated successfully!');
      } else {
        setMessage('Failed to update profile');
      }
    } catch (error) {
      setMessage('Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900/20 to-gray-900">
      <RealisticGalaxyBackground />
      <Navbar />
      
      <div className="relative z-10 container mx-auto px-4 pt-24 pb-12">
        <div className="max-w-2xl mx-auto">
          <div className="bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-purple-500/30 shadow-2xl">
            <h1 className="text-4xl font-bold text-white mb-2">Your Profile</h1>
            <p className="text-gray-300 mb-8">Customize your Aloha Queen Supernova experience</p>

            {message && (
              <div className={`p-4 rounded-lg mb-6 ${
                message.includes('success') ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'
              }`}>
                {message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-gray-300 mb-2">Full Name</label>
                <input
                  type="text"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-700/50 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Avatar Preference</label>
                <select
                  value={formData.avatar_preference}
                  onChange={(e) => setFormData({...formData, avatar_preference: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-700/50 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                >
                  <option value="queen">Queen Supernova</option>
                  <option value="king">King Supernova</option>
                  <option value="nexus">Nexus Bot</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 mb-2">Theme</label>
                <select
                  value={formData.theme}
                  onChange={(e) => setFormData({...formData, theme: e.target.value})}
                  className="w-full px-4 py-3 bg-gray-700/50 text-white rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                >
                  <option value="dark">Dark Mode</option>
                  <option value="light">Light Mode</option>
                </select>
              </div>

              <div className="bg-gray-700/30 p-6 rounded-lg">
                <h3 className="text-xl font-semibold text-white mb-2">Account Info</h3>
                <div className="space-y-2 text-gray-300">
                  <p><span className="font-medium">Email:</span> {user.email}</p>
                  <p><span className="font-medium">Subscription:</span> <span className="text-purple-400 font-semibold">{user.subscription_tier}</span></p>
                  <p><span className="font-medium">Member Since:</span> {new Date(user.created_at).toLocaleDateString()}</p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3 rounded-lg transition-all disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Save Changes'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;